import { SortedChainedMap, SortedChainedMapEntry } from "@cervval.js/common/src/tools/collection/sortedChainedMap";
import { MYSQL_DATE_FORMAT, TsDuration, UnixTimestamp } from "@cervval.js/common/src/tools/timestamps.tools.definitions";
import { TimestampTools } from "@cervval.js/common/tools";

/** Internal structure to store information about what has changed at a given timestamp */
interface ChangeEvent<Data = void> {
    /** Cumulated delta at the given instant. */
    usageDelta: number;
    /** Cached quantity of resource used (potentially undefined if not computed yet) */
    usage_cache?: number;

    // TODO add data
}

/** History of all change events */
type UsageTimeline<Data> = SortedChainedMap<UnixTimestamp, ChangeEvent<Data>>;

/** use/release parameters */
export type UsageOptions<Data> = {
    start?: UnixTimestamp,
    finish?: UnixTimestamp,
    data?: Data,
    skipRecomputeQuantities?: true
};

/** Find next availability options */
export type RelativeUsageOptions<Data> = {
    lag: TsDuration,
    duration: TsDuration,
    quantity: number,
    data?: Data
}

/** Cache of resource usages over time */
export class UsageCache<Data = void> {

    /** Quantity available for the resource */
    public quantity: number;
    /** Timeline of events */
    protected readonly _usageCache: UsageTimeline<Data>;

    /**
     * Constructor
     * @param quantity Quantity available for the resource
     * @param forceUsageCache Optional. Used by the clone method to pass a timeline
     */
    constructor(quantity: number, forceUsageCache?: UsageTimeline<Data>) {
        this.quantity = quantity;
        this._usageCache = forceUsageCache ?? new SortedChainedMap({
            buildDefaultValue: function () { return { usageDelta: 0 }; },
            cloneValue: function (value) { return { ...value }; }
        });
    }

    //#region Use / Release ---------------------------------------------------

    /**
     * Add a quantity of usage at given start instant and remove it at finish instant.
     * If start or finish are not provided, the quantity is added/subtracted all over the time.
     */
    public use(quantityToUse: number, options: UsageOptions<Data>): void {
        this._addOrDelete(quantityToUse, options);
    }

    /** Use all relative resource usages from instant */
    public useAll(instant: UnixTimestamp, usages: RelativeUsageOptions<Data>[]): void {
        for (const usage of usages) {
            // FIXME Use calendars
            const start = TimestampTools.add(instant, usage.lag).uts;
            const finish = TimestampTools.add(start, usage.duration).uts;
            this.use(usage.quantity, {
                start,
                finish
            });
        }
    }

    /** Subtract a quantity from resource on the given time period (add -quantity) */
    public release(quantityToRelease: number, options: UsageOptions<Data>): void {
        return this._addOrDelete(-quantityToRelease, options);
    }

    /** Release all relative resource usages */
    public releaseAll(instant: UnixTimestamp, usages: RelativeUsageOptions<Data>[]): void {
        for (const usage of usages) {
            // FIXME Use calendars
            const start = TimestampTools.add(instant, usage.lag).uts;
            const finish = TimestampTools.add(start, usage.duration).uts;
            this.release(usage.quantity, {
                start,
                finish
            });
        }
    }

    /** 
     * Common implementation for add/delete methods 
     * This function does :
     * - +delta at start instant (if any)
     * - -delta at finish instant (if any)
     * - add/delete the data from start/finish instant depending on mode
     */
    protected _addOrDelete(usageDelta: number, options: UsageOptions<Data>): void {
        if (options.start == null && options.finish == null) {
            // Used all over the time
            this.quantity -= usageDelta;
        } else {
            let startEntry: SortedChainedMapEntry<UnixTimestamp, ChangeEvent<Data>> | undefined = undefined;
            let finishEntry: SortedChainedMapEntry<UnixTimestamp, ChangeEvent<Data>> | undefined = undefined;
            // Added only at a given instant
            if (options.start != null) {
                startEntry = this._usageCache.getOrCreateEntry(options.start);
                startEntry.value.usageDelta += usageDelta; // Take the resources
                if (options.data != null) {
                    // TODO
                }
            }
            if (options.finish != null) {
                finishEntry = this._usageCache.getOrCreateEntry(options.finish);
                finishEntry.value.usageDelta -= usageDelta; // Release the resources
                if (options.data != null) {
                    // TODO
                }
            }

            if (options.skipRecomputeQuantities === true) {
                // Don't do anything
            } else {
                if (startEntry == null) {
                    startEntry = this._usageCache.front!; // We have added at least start or finish instant, so there is at least one entry
                }
                if (finishEntry == null) {
                    finishEntry = this._usageCache.back!; // We have added at least start or finish instant, so there is at least one entry
                }

                // Update quantities from start to finish entries
                let entry: SortedChainedMapEntry<UnixTimestamp, ChangeEvent<Data>> | undefined = startEntry;
                while (entry != null) {
                    // Get the previous quantity
                    const previousUsage = entry.prev == null ? 0 : entry.prev.value.usage_cache ?? 0;
                    entry.value.usage_cache = previousUsage + entry.value.usageDelta;
                    if (entry === finishEntry) {
                        // We have reached the finish instant, so we can stop
                        break;
                    }
                    entry = entry.next;
                }
            }
        }
    }

    //#endregion

    //#region Compute quantities cache ----------------------------------------

    /** Remove instants where delta is 0 to gain performance */
    public pack(): void {
        // Only keep instants where delta is not zero
        this._usageCache.filter(function (entry) { return entry.value.usageDelta !== 0; });
    }

    /** 
     * Force a recompute of the quantities cache for all resources.
     * Use this function after multiple add/sub with skipRecomputeQuantities 
     */
    public recompute(): void {
        let entry = this._usageCache.front;
        let used = 0;
        while (entry) {
            used += entry.value.usageDelta;
            entry.value.usage_cache = used;
            entry = entry.next;
        }
    }


    //#endregion

    //#region Utility functions -----------------------------------------------

    /** Get the quantity used at a given instant */
    public getUsageAt(instant: UnixTimestamp): { usage: number, from?: UnixTimestamp, to?: UnixTimestamp } {
        const from = this._usageCache.getFloorEntry(instant);
        const to = from == null ? this._usageCache.front : from.next;
        return {
            usage: from?.value.usage_cache ?? 0,
            from: from?.key,
            to: to?.key
        };

    }

    /** @returns The available quantity at given instant and the period of validity of the function */
    public getAvailabilityAt(instant: UnixTimestamp): { available: number, usage: number, from?: UnixTimestamp, to?: UnixTimestamp } {
        const usage = this.getUsageAt(instant);
        return {
            ...usage,
            available: this.quantity - usage.usage
        };
    }

    /** @returns Next instant where the resources will be available */
    public getNextAvailability(_instant: UnixTimestamp, usages: RelativeUsageOptions<Data>[]): UnixTimestamp | null {
        let reference: UnixTimestamp | null = _instant;
        while (reference != null) {
            // Keep for debug
            // console.log(`Reference : ${TimestampTools.format(reference, MYSQL_DATE_FORMAT)}`);
            // -- Merge all usages --
            const usagesToAdd = new SortedChainedMap<UnixTimestamp, { usage: number }>({
                buildDefaultValue: function () { return { usage: 0 }; },
                cloneValue: function (value) { return { ...value }; }
            });
            for (const usage of usages) {
                // FIXME Calendars

                if (usage.quantity === 0) {
                    // This neutral usage is ignored
                    continue;
                }

                const start = TimestampTools.add(reference, usage.lag).uts;
                const finish = TimestampTools.add(start, usage.duration).uts;

                usagesToAdd.getOrCreateEntry(start).value.usage += usage.quantity;
                usagesToAdd.getOrCreateEntry(finish).value.usage -= usage.quantity;
            }

            if (usagesToAdd.length === 0) {
                // No usage, so it's ok
                return reference;
            }

            // -- Check that necessary resources are available at the given dates --
            let toAddEntry: SortedChainedMapEntry<UnixTimestamp, { usage: number }> | null = usagesToAdd.front ?? null;
            let nextInstantToCheck: UnixTimestamp = toAddEntry?.key ?? reference; // Pass reference as default value, but won't be used if there is no toAddEntry to check
            let available = true;
            let cumulativeDelta = usagesToAdd.front?.value.usage ?? 0;
            while (toAddEntry != null) {
                const availability = this.getAvailabilityAt(nextInstantToCheck);
                if (cumulativeDelta === 0) {
                    // Don't check, since we don't modify the usage, we don't account for existing problems
                } else {
                    // Check if we do have enough resource
                    // Keep for debug
                    // console.log(`- Check ${cumulativeDelta} of resource is available at ${TimestampTools.format(toAddEntry.key, MYSQL_DATE_FORMAT)}`);
                    if (availability.available < cumulativeDelta) {
                        // Resource availability < Required usage
                        available = false;
                        // Quick exit
                        if (availability.to == null) {
                            // In this case, we are sure that the resource availability will never be sufficient since there is no more events
                            return null;
                        }
                        // Align nextInstantToCheck with the current instant in usagesToAdd that was problematic.
                        // We will use it to shift the reference later on
                        nextInstantToCheck = usagesToAdd.getFloorEntry(nextInstantToCheck)!.key; // Can't be null since nextInstantToCheck is always increasing
                        // Stop testing the availability
                        break;
                    }
                }

                // Here available is necessarily true, so we need to continue checking at the next change, it can be either:
                // - the next change of required resource quantity in temporaryTimeline
                // - the next change of resource availability in the current timeline
                if (toAddEntry.next == null) {
                    // If we got here, it means we already checked the last instant in the usages
                    // we can safely exit the loop.
                    // MAB,ACL: If we don't exit here, we will keep testing resource availability until the end of time.
                    // This can cause issue if resource availability is invalid after the end of the RUs.
                    // See test "Checks next availability with existing usage overflow".
                    break;
                }
                // Here, toAddEntry.next.key cannot be null so we will at least retrieve this value
                const tmpNextInstantToCheck = TimestampTools.min(toAddEntry.next.key, availability.to)!;
                nextInstantToCheck = tmpNextInstantToCheck;

                if (nextInstantToCheck === toAddEntry.next?.key) {
                    cumulativeDelta += toAddEntry.next.value.usage;
                    toAddEntry = toAddEntry.next ?? null;
                }
            }

            if (available === true) {
                // All requirements are fulfilled, this instant is correct
                return reference;
            } else {
                // Here we take as reference the first instant were resource was not available.
                const virtualReference = nextInstantToCheck;

                // Next, we look for the next instant to test: the next change in the existing timeline
                const nextVirtualReference = this._usageCache.getNextEntry(virtualReference)?.key;
                if (nextVirtualReference == null) {
                    // No more alignment to test, we did not found a solution
                    return null;
                } else {
                    // We move the reference such that virtualReference ==> nextVirtualReference
                    const virtualLag = TimestampTools.diff(virtualReference, reference, "second");
                    reference = TimestampTools.add(nextVirtualReference, { second: virtualLag }).uts;
                }
            }
        }
        // Not found
        return null;
    }

    //#endregion

    //#region Management ------------------------------------------------------

    /** Save current state. Can be restored quickly with restore state. */
    public clone(): UsageCache<Data> {
        return new UsageCache(this.quantity, this._usageCache.clone());
    }

    //#endregion

    //#region Debug -----------------------------------------------------------

    /** Get a debug string */
    public debug(prefix: string = "  ", separator: string = "\n"): string {
        const result: string[] = [];

        let usage = this._usageCache.front;
        if (usage == null) {
            result.push(`^${prefix}Q: ${this.quantity}, No usage`);
        } else {
            while (usage != null) {
                result.push(`${prefix}[${TimestampTools.format(usage.key, MYSQL_DATE_FORMAT)}] U: ${usage.value.usage_cache ?? "--"}, A: ${usage.value.usage_cache == null ? "--" : this.quantity - usage.value.usage_cache}, Q: ${this.quantity}`);
                usage = usage.next;
            }
        }

        return result.join(separator);
    }

    /** @throws If consistency is not correct */
    public checkConsistency(): void {
        this._usageCache.checkConsistency();
    }

    //#endregion
}